import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { MapPin, Clock, Phone } from 'lucide-react';

export default function VisitInfoPage() {
  const [formData, setFormData] = useState({
    name: '',
    phone: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.phone) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/visit-info`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitted(true);
      } else {
        toast.error(data.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="visit-info-page">
        <div className="mx-auto max-w-2xl px-6 sm:px-10 py-12">
          <div className="text-center">
            <div className="mb-8">
              <div className="w-16 h-px bg-gray-300 mx-auto mb-8" />
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-gray-900 leading-tight italic mb-8">
                See You There
              </h1>
              <div className="w-16 h-px bg-gray-300 mx-auto" />
            </div>

            <div className="mt-12 space-y-6">
              <div className="flex items-start gap-4 justify-center text-left">
                <MapPin className="w-6 h-6 text-gray-600 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-[15px] font-medium text-gray-900 mb-1">Location</p>
                  <p className="text-[15px] text-gray-600 font-body leading-relaxed">
                    225-07 139th Ave<br />
                    Jamaica, NY 11413
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 justify-center text-left">
                <Clock className="w-6 h-6 text-gray-600 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-[15px] font-medium text-gray-900 mb-1">Time</p>
                  <p className="text-[15px] text-gray-600 font-body">7:30 PM</p>
                </div>
              </div>

              <div className="flex items-start gap-4 justify-center text-left">
                <Phone className="w-6 h-6 text-gray-600 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-[15px] font-medium text-gray-900 mb-1">Questions? Call us</p>
                  <p className="text-[15px] text-gray-600 font-body">
                    <a href="tel:+18623006513" className="hover:text-gray-900 transition-colors">
                      (862) 300-6513
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center" data-testid="visit-info-page">
      <div className="mx-auto max-w-xl px-6 sm:px-10 py-12">
        <div className="text-center mb-12">
          <div className="w-16 h-px bg-gray-300 mx-auto mb-8" />
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-gray-900 leading-tight italic">
            Join Us
          </h1>
          <div className="w-16 h-px bg-gray-300 mx-auto mt-8" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="visit-info-form">
          <div>
            <Label htmlFor="name" className="block text-[12px] uppercase tracking-[0.1em] text-gray-500 font-body mb-2">
              Name
            </Label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full"
              data-testid="name-input"
              required
            />
          </div>

          <div>
            <Label htmlFor="phone" className="block text-[12px] uppercase tracking-[0.1em] text-gray-500 font-body mb-2">
              Phone Number
            </Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full"
              data-testid="phone-input"
              required
            />
          </div>

          <div className="pt-4 text-center">
            <Button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center h-12 px-10 border border-gray-900 bg-transparent text-gray-900 font-body text-[13px] font-medium uppercase tracking-[0.15em] hover:bg-gray-900 hover:text-white transition-colors duration-200 disabled:opacity-50"
              data-testid="submit-button"
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
